import type { WorldCapabilities } from "@uzverse/contracts";
import { UzError } from "@uzverse/core";
import { quatToYaw, radToDeg } from "@uzverse/world-scene";
import type { PlacementResult, SceneNode } from "@uzverse/world-scene";
import type { InvokeResult } from "../api.js";
import { footprintsOf } from "../footprints.js";
import type { Footprint } from "../footprints.js";
import {
  canArrange,
  hitFootprint,
  normalizeYawDeg,
  parcelsOf,
  placementWorld,
  previewPlacement,
  rotateQuarter,
} from "./rules.js";
import type { Arrangeable, Pose, SceneParcel } from "./rules.js";

/** How long a dropped ghost waits for the confirming world delta before it snaps back. */
export const CONFIRM_TIMEOUT_MS = 8000;
/** How long the undo chip stays. */
export const CHIP_MS = 12_000;

export interface ToastSpec {
  kind: "error" | "info";
  /** i18n key inside the world namespace. */
  key: string;
  params?: Record<string, string | number>;
}

export interface DragState {
  id: string;
  /** Pointer to center offset at grab time (so the building does not jump under the cursor). */
  grab: [number, number];
  start: Pose;
  pose: Pose;
  valid: boolean;
  reason: Exclude<PlacementResult, { ok: true }>["reason"] | null;
}

export interface PendingMove {
  id: string;
  from: Pose;
  to: Pose;
  expectedVersion: number;
  /** Set once the service answered ok; the ghost then waits for the delta. */
  accepted: { version: number | null; correlationId: string | null } | null;
}

export interface UndoChip {
  id: string;
  label: string;
  from: Pose;
  to: Pose;
  correlationId: string | null;
  /** Version of the building after the move (for the fallback undo). */
  version: number | null;
}

export interface BlueprintState {
  drag: DragState | null;
  pending: PendingMove | null;
  chip: UndoChip | null;
  /** The pointer is over a building the user may arrange (cursor hint). */
  hoverId: string | null;
}

export interface BlueprintDeps {
  nodes: () => ReadonlyMap<string, SceneNode>;
  caps: () => Pick<WorldCapabilities, "canEditMap" | "arrangeableParcelIds" | "parcels"> | null;
  invoke: (name: string, input: unknown) => Promise<InvokeResult>;
  toast: (toast: ToastSpec) => void;
  grid?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

const poseOf = (n: SceneNode): Pose => ({
  x: n.transform.position[0],
  z: n.transform.position[2],
  yawDeg: normalizeYawDeg(radToDeg(quatToYaw(n.transform.rotation) ?? 0)),
});

const samePose = (a: Pose, b: Pose): boolean =>
  a.x === b.x && a.z === b.z && normalizeYawDeg(a.yawDeg) === normalizeYawDeg(b.yawDeg);

/** i18n key of a server error. */
export function errorKey(error: unknown): { key: string; params: Record<string, string> } {
  if (error instanceof UzError) {
    const known = ["conflict", "permission_denied", "invalid_input", "not_found", "unavailable"];
    return {
      key: `blueprint.error.${known.includes(error.code) ? error.code : "other"}`,
      params: { detail: error.message },
    };
  }
  return { key: "blueprint.error.other", params: { detail: "" } };
}

/**
 * The blueprint drag state machine: grab an arrangeable building, preview a snapped (and checked)
 * ghost, drop it, call `world.moveBuilding`, keep the ghost until the live delta confirms and snap
 * back with a toast on any rejection. No React and no three.js, so it is unit tested directly.
 */
export class BlueprintController {
  private state: BlueprintState = { drag: null, pending: null, chip: null, hoverId: null };
  private listeners = new Set<() => void>();
  private confirmTimer: unknown = null;
  private chipTimer: unknown = null;
  private readonly grid: number | undefined;

  constructor(private readonly deps: BlueprintDeps) {
    this.grid = deps.grid;
  }

  getState = (): BlueprintState => this.state;

  subscribe = (l: () => void): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  private set(patch: Partial<BlueprintState>): void {
    this.state = { ...this.state, ...patch };
    for (const l of this.listeners) l();
  }

  private timer(fn: () => void, ms: number): unknown {
    return (this.deps.setTimer ?? setTimeout)(fn, ms);
  }

  private clear(handle: unknown): void {
    if (handle !== null) (this.deps.clearTimer ?? ((h) => clearTimeout(h as never)))(handle);
  }

  // ------------------------------------------------------------------ queries

  private context() {
    const nodes = this.deps.nodes();
    const list = [...nodes.values()];
    const parcels = parcelsOf(list);
    const footprints = footprintsOf(list);
    return { nodes, parcels, footprints, world: placementWorld(list, parcels) };
  }

  /** Rule for a building, or null when the user may not move it. */
  arrangeable(id: string): Arrangeable | null {
    const { footprints, parcels } = this.context();
    const fp = footprints.find((f) => f.id === id);
    return fp ? canArrange(fp, this.deps.caps(), parcels) : null;
  }

  /** Ids of the buildings that show a drag handle. */
  handles(): Footprint[] {
    const { footprints, parcels } = this.context();
    const caps = this.deps.caps();
    return footprints.filter((f) => canArrange(f, caps, parcels) !== null);
  }

  /** Parcels the user may arrange inside (drawn highlighted). */
  arrangeableParcels(): SceneParcel[] {
    const caps = this.deps.caps();
    if (!caps) return [];
    const { parcels } = this.context();
    return caps.canEditMap
      ? parcels
      : parcels.filter((p) => caps.arrangeableParcelIds.includes(p.id));
  }

  // ------------------------------------------------------------------ pointer

  hover(x: number, z: number): void {
    if (this.state.drag) return;
    const hit = this.hit(x, z);
    const id = hit?.id ?? null;
    if (id !== this.state.hoverId) this.set({ hoverId: id });
  }

  private hit(x: number, z: number): Footprint | null {
    const { footprints, parcels } = this.context();
    const caps = this.deps.caps();
    return hitFootprint(footprints, x, z, (f) => canArrange(f, caps, parcels) !== null);
  }

  /** Starts a drag when the ground point is on a building the user may move. Returns whether it did. */
  pointerDown(x: number, z: number): boolean {
    if (this.state.pending) return false; // one move at a time: wait for its confirmation
    const fp = this.hit(x, z);
    if (!fp) return false;
    const start: Pose = poseOf(fp.node);
    this.set({
      drag: {
        id: fp.id,
        grab: [fp.x - x, fp.z - z],
        start,
        pose: start,
        valid: true,
        reason: null,
      },
    });
    return true;
  }

  pointerMove(x: number, z: number): void {
    const drag = this.state.drag;
    if (!drag) return;
    this.preview(drag, { x: x + drag.grab[0], z: z + drag.grab[1], yawDeg: drag.pose.yawDeg });
  }

  private preview(drag: DragState, raw: { x: number; z: number; yawDeg: number }): void {
    const { footprints, parcels, world } = this.context();
    const fp = footprints.find((f) => f.id === drag.id);
    const rule = fp ? canArrange(fp, this.deps.caps(), parcels) : null;
    if (!fp || !rule) {
      this.cancel();
      return;
    }
    const { pose, result } = previewPlacement(fp, raw, rule, world, this.grid);
    this.set({
      drag: { ...drag, pose, valid: result.ok, reason: result.ok ? null : result.reason },
    });
  }

  /** `R`: a quarter turn of the dragged ghost, or of the given (selected) building in place. */
  rotate(selectedId?: string | null): void {
    const drag = this.state.drag;
    if (drag) {
      this.preview(drag, {
        x: drag.pose.x,
        z: drag.pose.z,
        yawDeg: rotateQuarter(drag.pose.yawDeg),
      });
      return;
    }
    if (!selectedId || this.state.pending) return;
    const { footprints, parcels, world } = this.context();
    const fp = footprints.find((f) => f.id === selectedId);
    const rule = fp ? canArrange(fp, this.deps.caps(), parcels) : null;
    if (!fp || !rule) return;
    const from = poseOf(fp.node);
    const { pose, result } = previewPlacement(
      fp,
      { x: from.x, z: from.z, yawDeg: rotateQuarter(from.yawDeg) },
      rule,
      world,
      this.grid,
    );
    if (!result.ok) {
      this.deps.toast({ kind: "error", key: `blueprint.reason.${result.reason}` });
      return;
    }
    void this.commit(fp.id, from, pose, fp.node.version, fp.node.label ?? "");
  }

  cancel(): void {
    if (this.state.drag) this.set({ drag: null });
  }

  /** Drop: a valid, changed pose is sent to the service; an invalid one snaps back with a toast. */
  pointerUp(): void {
    const drag = this.state.drag;
    if (!drag) return;
    this.set({ drag: null });
    if (samePose(drag.pose, drag.start)) return;
    if (!drag.valid) {
      this.deps.toast({
        kind: "error",
        key: `blueprint.reason.${drag.reason ?? "outside_world"}`,
      });
      return;
    }
    const node = this.deps.nodes().get(drag.id);
    if (!node) return;
    void this.commit(drag.id, drag.start, drag.pose, node.version, node.label ?? "");
  }

  // ------------------------------------------------------------------ server round trip

  private async commit(
    id: string,
    from: Pose,
    to: Pose,
    expectedVersion: number,
    label: string,
  ): Promise<void> {
    const pending: PendingMove = { id, from, to, expectedVersion, accepted: null };
    this.set({ pending, chip: null });
    this.clear(this.confirmTimer);
    this.confirmTimer = this.timer(
      () => this.revert("blueprint.error.unconfirmed"),
      CONFIRM_TIMEOUT_MS,
    );
    try {
      const result = await this.deps.invoke("world.moveBuilding", {
        placementId: id,
        position: [to.x, 0, to.z],
        yawDeg: to.yawDeg,
        expectedVersion,
      });
      if (this.state.pending !== pending) return;
      const out = result.output as { version?: unknown } | null;
      pending.accepted = {
        version: typeof out?.version === "number" ? out.version : null,
        correlationId: result.correlationId,
      };
      this.set({ chip: null });
      this.chipLabel = label;
      this.settle();
    } catch (error) {
      if (this.state.pending !== pending) return;
      const { key, params } = errorKey(error);
      this.revert(key, params);
    }
  }

  private chipLabel = "";

  /** Snaps the ghost back (and says why). */
  private revert(key: string, params?: Record<string, string | number>): void {
    this.clear(this.confirmTimer);
    this.confirmTimer = null;
    if (!this.state.pending) return;
    this.set({ pending: null });
    this.deps.toast({ kind: "error", key, ...(params ? { params } : {}) });
  }

  /** Called whenever the scene changed: the live delta confirms an accepted move. */
  settle(): void {
    const pending = this.state.pending;
    if (!pending) return;
    const node = this.deps.nodes().get(pending.id);
    if (!node) {
      this.clear(this.confirmTimer);
      this.set({ pending: null });
      return;
    }
    if (!pending.accepted) return; // the service has not answered yet
    const confirmed = node.version > pending.expectedVersion || samePose(poseOf(node), pending.to);
    if (!confirmed) return;
    this.clear(this.confirmTimer);
    this.confirmTimer = null;
    const chip: UndoChip = {
      id: pending.id,
      label: this.chipLabel,
      from: pending.from,
      to: pending.to,
      correlationId: pending.accepted.correlationId,
      version: node.version,
    };
    this.set({ pending: null, chip });
    this.clear(this.chipTimer);
    this.chipTimer = this.timer(() => this.set({ chip: null }), CHIP_MS);
  }

  /**
   * Undo chip: `history.undo` with the correlation id of the move. When the service did not report
   * one (TODO: the world tools return `correlationId`; until then), move the building back.
   */
  async undo(): Promise<void> {
    const chip = this.state.chip;
    if (!chip) return;
    this.clear(this.chipTimer);
    this.set({ chip: null });
    try {
      if (chip.correlationId) {
        await this.deps.invoke("history.undo", { correlationId: chip.correlationId });
      } else {
        const node = this.deps.nodes().get(chip.id);
        await this.deps.invoke("world.moveBuilding", {
          placementId: chip.id,
          position: [chip.from.x, 0, chip.from.z],
          yawDeg: chip.from.yawDeg,
          ...(node ? { expectedVersion: node.version } : {}),
        });
      }
      this.deps.toast({ kind: "info", key: "blueprint.undone" });
    } catch (error) {
      const { key, params } = errorKey(error);
      this.deps.toast({ kind: "error", key, params });
    }
  }

  dispose(): void {
    this.clear(this.confirmTimer);
    this.clear(this.chipTimer);
    this.listeners.clear();
  }
}
